import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { authLogin } from '@/services/api/auth';
import { AuthMethod, IdentityScope } from '@/services/api/uc';

/**
 * Hook for handling redirection to the author platform with automatic authentication
 */
export const useAuthorRedirect = () => {
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, user } = useAuth();

  /**
   * Removes token prefix if present
   */
  const removeTokenPrefix = (str: string, prefix: string): string => {
    if (str.startsWith(prefix)) {
      return str.substring(prefix.length);
    }
    return str;
  };

  /**
   * Redirects to author platform, authenticating user if logged in
   */
  const goToAuthorPlatform = async (): Promise<void> => {
    const authorPlatformUrl = import.meta.env.VITE_AUTHOR_PLATFORM_URL || '';
    const authorCookieName = import.meta.env.VITE_AUTHOR_COOKIE_NAME || '';

    // 从cookie获取token
    const cookieToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('anzir_token='))
      ?.split('=')[1];

    // If user is not authenticated or no token in cookie, just redirect
    if (!isAuthenticated || !cookieToken) {
      console.log("用户未登录或没有找到token，直接跳转到作者平台");
      // Clear any existing author cookies before redirecting
      document.cookie = `${authorCookieName}=; domain=.anzirtech.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      window.location.href = `${authorPlatformUrl}redirect?from=website`;
      return;
    }

    try {
      setLoading(true);

      // 清理token前缀
      const prefix = 'Anzir ';
      const cleanToken = removeTokenPrefix(cookieToken, prefix);

      console.log("开始请求作者平台Token交换...");
      console.log("使用的Token:", cleanToken ? "Token已获取" : "Token为空");

      // 准备设备信息参数
      const deviceInfo = {
        deviceId: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
        deviceType: "web",
        appVersion: import.meta.env.VITE_APP_VERSION || "1.0.0"
      };

      // 准备登录参数
      const params = {
        certificateIdentification: cleanToken,
        certificate: cleanToken,
        device: deviceInfo,
        identityScope: IdentityScope.Author,
        authMethod: AuthMethod.TokenReLogin
      };

      // 调用认证API
      const response = await authLogin(params);
      console.log("作者平台Token交换结果:", response);

      if (response.code === 1000 && response.data?.token) {
        console.log("作者平台Token交换成功");

        // 清除现有cookie
        document.cookie = `${authorCookieName}=; domain=.anzirtech.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

        // 设置新cookie，包含作者token
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 7); // 设置cookie有效期为7天

        // 使用正确的cookie名称
        document.cookie = `${authorCookieName}=Anzir ${response.data.token}; domain=.anzirtech.com; path=/; expires=${expirationDate.toUTCString()}`;

        // 跳转到作者平台
       window.location.href = `${authorPlatformUrl}redirect?from=website`;
      } else {
        console.error('获取作者token失败:', response);
        // 如果token交换失败，降级为直接跳转
        window.location.href = `${authorPlatformUrl}redirect?from=website`;
      }
    } catch (error) {
      console.error('跳转到作者平台时出错:', error);
      // 如果出现错误，降级为直接跳转
      window.location.href = `${authorPlatformUrl}redirect?from=website`;
    } finally {
      setLoading(false);
    }
  };

  return {
    goToAuthorPlatform,
    loading
  };
};
